import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import {
  FacebookShareButton,
  TelegramShareButton,
  VKShareButton
} from 'react-share'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import ReactHintFactory from 'react-hint'
import GoogleMap from 'google-map-react'
import ReactModal from 'react-modal'

import { inject, observer } from 'mobx-react'
import InfiniteScroll from 'react-infinite-scroller'
import ReactSVG from 'react-svg'
import Layout from '../components/Layout'
import { i18n, withTranslation, Router } from '../i18n'
import Footer from '../components/Footer'
import RouteCard from '../components/RouteCard'
import {
  Container, ProgramList, ProgramSection, GlobalStyle
} from '../styles/programStyles'
import { favoriteRoute, fetchPartnerRoutes, getEntityByID } from '../api/routes'
import { differSocialsLinks, matchPlural, normalizeDurationTime } from '../api/utils'
import Button from '../components/Button'
import { PerformanceWidget } from '../components/PerformanceWidget'
import { MapWidget } from '../components/MapWidget'
import SocialIcons from '../components/SocialIcons'
import { StarWidget } from '../components/StarWidget'
import icons, { socialsDomains } from '../constants/socialIcons'
import PDFButton from '../components/PDFButton'
import Loading from '../components/Loading'
import programTypeMap from '../constants/programTypes'
import mapStyles from '../styles/mapStyles'

import getConfig from 'next/config'

const CustomButton = styled(Button)`
    max-width: 839px;
    width: 100%;
    display: flex;
    align-items: center;
    height: 60px;
    margin-top: 58px;
    margin-bottom: 40px;
    align-self: flex-end;
    @media (max-width: 1200px) {
      max-width: 628px;
      align-self: center;
      margin-top: 18px;
    }
    @media (max-width: 700px) {
      max-width: 100%;
      margin-left: 40px;
      margin-top: 0;
      display: flex;
    }
`

const { publicRuntimeConfig } = getConfig()

@inject('store') @observer
class Program extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    store: PropTypes.instanceOf(Object).isRequired,
    host: PropTypes.string
  }

  static defaultProps = {
    host: undefined
  }

  static async getInitialProps({ req, query, mobxStore }) {
    const { token, id: userId } = mobxStore.user
    const { programType, id } = query
    let { program } = query
    const language = req ? req.language : i18n.language

    if (token && mobxStore.user.performances.length <= 0) {
      await mobxStore.updateUser()
    }

    if (id && programType && (id !== userId || mobxStore.program.id !== id)) {
      program = await getEntityByID(id, programTypeMap[programType], language, token)
      mobxStore.setProgram(program, programType)
    }

    if (mobxStore.partnerRoutes.length <= 0 || mobxStore.partnerRoutes.length === 4) {
      const partnerRoutes = await fetchPartnerRoutes(0, { random_order: true }, 50, language, token)
      mobxStore.setPartnerRoutes(partnerRoutes.results)
    }

    if (programType) {
      mobxStore.setProgramType(programType)
    }

    if (program && program.length > 0) {
      mobxStore.setProgram(program, programType)
    }

    return {
      namespacesRequired: ['common', 'program', 'about', 'contacts', 'auth', 'reset-password'],
      host: typeof window === 'undefined' ? req.headers.host : window.location.host
    }
  }

  updateDimensions = () => {
    this.setState({ windowWidth: window.innerWidth })
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions)
  }

  state = {
    isOpenClosingModal: false,
    performanceIdToDelete: '',
    windowWidth: undefined
  }

  onStarPress = (id, token, favorited, updateProgram, toggleLoginModal) => {
    if (!token) {
      toggleLoginModal(true, this.props.t('common:make_an_account'))
    } else {
      const entity = this.props.store.programType === 'partner' ? 'partner_routes' : 'profiles'
      favoriteRoute(id, entity, undefined, token, favorited)
        .then(() => {
          updateProgram(entity)
        })
    }
  }

  componentDidMount() {
    Router.events.on('routeChangeStart', ((url) => {
      if (url === '/program' || url === '/en/program') {
        this.props.store.setProgramType(null)
        this.props.store.updateRoutes()
      }
    }))
    i18n.on('languageChanged', () => {
      this.props.store.updateProgram()
    })
    window.addEventListener('resize', this.updateDimensions)
    this.updateDimensions()
  }


  render() {
    const {
      t, store, store: {
        user, user: { token }, partnerRoutes, updateRoute, toggleLoginModal, updateProgram, updateRoutes,
        programType, program, program: { performances, social_profile_link }, setSelectedPerformance, isMyProgram
      },
      host
    } = this.props

    const { isOpenClosingModal, performanceIdToDelete, windowWidth } = this.state
    const ReactHint = ReactHintFactory(React)
    const isServer = typeof window === 'undefined'
    const href = `${host}/program/profile/${user.id}`

    return (
      <Layout meta={{ title: t('program:PAGE_PROGRAM_SEO_TITLE'), description: t('program:PAGE_PROGRAM_SEO_DESC'), concatName: false }}>
        <GlobalStyle />
        <ReactModal
          ariaHideApp={false}
          isOpen={isOpenClosingModal}
          onRequestClose={() => {
            this.setState({
              isOpenClosingModal: false,
              performanceIdToDelete: undefined
            })
          }}
          overlayClassName="ClosingModal_Overlay"
          className="ClosingModal_Content"
        >
          <div className="closeWrapper">
            <h3>{t('program:delete_artist')}</h3>
            <p className="sureDelete">{t('program:sure_delete_artist')}</p>
            <div className="modalButtons">
              <Button
                onPress={() => {
                  favoriteRoute(performanceIdToDelete, 'performances', undefined, token, true)
                    .then(() => {
                      updateProgram(programTypeMap[programType])
                      updateRoutes()
                      this.setState({
                        performanceIdToDelete: undefined,
                        isOpenClosingModal: false
                      })
                    }).catch(err => console.warn({ err }))
                }}
              >
                {t('common:delete')}
              </Button>
              <Button
                light
                onPress={() => {
                  this.setState({ isOpenClosingModal: false })
                }}
              >
                {t('common:cancel')}
              </Button>
            </div>
          </div>
        </ReactModal>
        <Container>
          {windowWidth > 1200 && (
            <div className="map">
              <GoogleMap
                bootstrapURLKeys={{ key: publicRuntimeConfig.googleApiKey }}
                defaultZoom={14}
                defaultCenter={{
                  lat: 56.8275,
                  lng: 60.6025
                }}
                options={{
                  styles: mapStyles
                }}
              >
                {
                  (performances.length > 0 && (programType !== null && programType !== undefined))
                  && performances.map(({
                    id,
                    selected,
                    place: {
                      phone, photo, address, coordinates: [lat, lng]
                    }
                  }) => (
                    <MapWidget
                      key={id}
                      className="mapWidget"
                      phone={phone}
                      photo={photo}
                      address={address}
                      lat={lat}
                      lng={lng}
                      onPress={() => {
                        if (selected) setSelectedPerformance()
                        else setSelectedPerformance(id)
                      }}
                      closed={!selected}
                    />
                  ))
                }
              </GoogleMap>
            </div>
          )}
          <ProgramSection
            program={programType !== null && programType !== undefined}
            emptyProgram={performances.length <= 0}
          >
            {/* {(programType === 'partner') && (
            <img className="partnerLogo" src={program.logo} alt="partner logo" />
            )} */}
            <div className={`programSectionWrap ${programType}`}>
              {windowWidth > 1200 && <div className="sideTear" />}
              {programType !== 'partner'
                && (((user.performances.length <= 0 && user.id) || (!user.id && programType !== 'profile')) ? (
                  <div className="emptyProgram">
                    <img src="/static/images/empty-program-back@2x.png" alt="" />
                    <span className="emptyProgramHeader">{t('program:EMPTY_PROGRAM_HEADER')}</span>
                    <span className="emptyProgramMessage">{t('program:EMPTY_PROGRAM_MESSAGE')}</span>
                  </div>
                ) : (programType === 'profile' ? (
                  (isMyProgram && performances.length > 0) && (
                  <div className="programNav">
                    <Button link="/program">{t('program:recommendations')}</Button>
                    <ReactHint
                      className="customHint"
                      events={{ click: true }}
                      onRenderContent={() => (
                        <div
                          className="customHintContent"
                          ref={(ref) => {
                            if (ref) {
                              this.hintText = ref
                              setTimeout(() => {
                                this.hintText.className += ' active'
                                setTimeout(() => {
                                  this.hintText.className = 'customHintContent'
                                }, 2000)
                                setTimeout(() => {
                                  this.hint.toggleHint()
                                }, 3000)
                              }, 0)
                            }
                          }}
                        >
                          <span className="hintText">{t('program:link_copied')}</span>
                        </div>
                      )}
                      ref={ref => this.hint = ref}
                    />
                    {windowWidth && windowWidth > 730
                      && (
                      <>
                        <button className="navButton navIcon" data-rh>
                          <CopyToClipboard text={href}>
                            <ReactSVG src="/static/images/share.svg" />
                          </CopyToClipboard>
                        </button>
                        <VKShareButton
                          url={`https://${href}`}
                          className="navIcon"
                        >
                          {icons.vkontakte({})}
                        </VKShareButton>
                        {/* <FacebookShareButton url={href} className="navIcon">
                          {icons.facebook({})}
                        </FacebookShareButton> */}
                        <TelegramShareButton url={href} className="navIcon">
                          {icons.telegram({})}
                        </TelegramShareButton>
                        {!isServer ? <PDFButton program={program} className="navIcon" />
                          : <Loading className="navIcon" />}
                      </>
                      )}
                  </div>
                  ))
                  : (
                    <CustomButton
                      link={`/program?programType=profile&id=${user.id}`}
                      humanLink={`/program/profile/${user.id}`}
                    >
                      {t('program:my_program')}
                    </CustomButton>
                  )
                ))}
              {(programType === null || (user.performances.length <= 0 && programType === 'profile' && user.id)) && (
                <div className="routesListWrapper">
                  <h2 className="header">{t('program:RECOMMENDED_ROUTES')}</h2>
                  <div className="routesSection">
                    <div className="scrollContainer">
                      <InfiniteScroll
                        className="infiniteScroll"
                        pageStart={0}
                        loadMore={() => {
                          // console.log('log more')
                        }}
                        hasMore={false}
                        useWindow={false}
                        getScrollParent={() => document.getElementById('list')}
                        loader={(
                          <div className="loader" key={0}>
                            {t('program:loading')}
                          </div>
                        )}
                      >
                        {partnerRoutes.length > 0 ? (partnerRoutes.map(route => (
                          <RouteCard
                            key={route.id}
                            route={route}
                            className="programPageStyle"
                            program
                            onFavorite={() => {
                              if (!token) {
                                toggleLoginModal(true, t('common:make_an_account'))
                              } else {
                                favoriteRoute(route.id, 'partner_routes', undefined, token, route.favorited)
                                  .then(() => {
                                    updateProgram()
                                    updateRoute(route.id)
                                  })
                                  .catch((er) => {
                                    console.warn('error', er)
                                  })
                              }
                            }}
                          />
                        ))) : (
                          <div className="noRoutesYet">
                            {t('common:no_routes_yet')}
                          </div>
                        )}
                      </InfiniteScroll>
                    </div>
                  </div>
                </div>
              )}
              {programType === 'partner' && (
                <div className="partnerDescription">
                  <span className="routeFrom">{`${t('program:route')} ${program.name_preposition}`}</span>
                  <h1 className="routeName">{program.name}</h1>
                  <div className="programCounts">
                    <span className="artistsCount">
                      {`${program.artist_count} ${matchPlural(program.artist_count,
                        [t('about:artist_one'), t('about:artist_few'), t('about:artist_many')])} / ${
                        program.place_count} ${matchPlural(program.place_count,
                        [t('about:place_one'), t('about:place_few'), t('about:place_many')])}`
                      }
                    </span>
                    <span className="programDuration">
                      {`${Math.floor(program.duration / 60)} ${matchPlural(Math.floor(program.duration / 60),
                        [t('about:hour_one'), t('about:hour_few'), t('about:hour_many')])}`}
                    </span>
                  </div>
                  <p className="description">{program.description}</p>
                  {store.otherLinks.length > 0 && (
                    <div className="linksDiv">
                      {store.otherLinks.map(link => (
                        <a
                          href={link}
                          className="descriptionLink"
                          target="_blank"
                          rel="noopener noreferrer"
                          key={link}
                        >
                          {link}
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="widgets">
                    <SocialIcons
                      socials={store.socials}
                      large
                      className="socials"
                    />
                    <StarWidget
                      className="starWidget"
                      starsCount={program.favorited_count}
                      filled={program.favorited}
                      onPress={() => {
                        this.onStarPress(program.id, token, program.favorited, updateProgram, toggleLoginModal)
                      }}
                    />
                  </div>
                </div>
              )}
              {(programType === 'profile' && !isMyProgram) && (
                <div className="profileSection">
                  <div className="avatarSection">
                    {program.avatar ? <img className="avatar" src={program.avatar} alt="avatar" />
                      : (
                        <ReactSVG
                          className="avatarSvgPlaceholder"
                          src="/static/images/default-user-avatar.svg"
                        />
                      )}
                    <div className="profileName">
                      <h2 className="routeFrom profileRouteName">{t('about:route_by')}</h2>
                      <span className="routeName profileRouteName">
                        {program.social_profile_link ? `${program.first_name} ${program.last_name}` : program.email}
                      </span>
                    </div>
                  </div>
                  <div className="programCounts center">
                    <span className="artistsCount">
                      {`${program.artist_count} ${matchPlural(program.artist_count,
                        [t('about:artist_one'), t('about:artist_few'), t('about:artist_many')])} / ${
                        program.place_count} ${matchPlural(program.place_count,
                        [t('about:place_one'), t('about:place_few'), t('about:place_many')])}`
                      }
                    </span>
                    <span className="programDuration">
                      {`${Math.floor(program.duration / 60)} ${matchPlural(Math.floor(program.duration / 60),
                        [t('about:hour_one'), t('about:hour_few'), t('about:hour_many')])}`}
                    </span>
                  </div>
                  <div className="widgets">
                    {social_profile_link ? (
                      <div className="linksDiv">
                        <SocialIcons
                          socials={differSocialsLinks([program.social_profile_link], socialsDomains).socials}
                          large
                        />
                      </div>
                    ) : (
                      <a href={`mailto:${program.email}`}>
                        <ReactSVG
                          className="emailSvg"
                          src="/static/images/envelop.svg"
                        />
                      </a>
                    )}
                    <StarWidget
                      className="starWidget"
                      starsCount={program.favorited_count}
                      filled={program.favorited}
                      onPress={() => {
                        this.onStarPress(program.id, token, program.favorited, updateProgram, toggleLoginModal)
                      }}
                    />
                  </div>
                </div>
              )
              }
              {(programType !== null) && (performances.length > 0 ? (
                <ProgramList>
                  <div className="programList">
                    <div className="programListWrapper">
                      {(performances) && (performances.map(({
                        favorited, artist, place, start_datetime, end_datetime, id, selected
                      }, i) => (
                        <PerformanceWidget
                          key={id}
                          filled={favorited}
                          first={i === 0}
                          last={i === performances.length - 1}
                          artist={artist}
                          place={place}
                          time={normalizeDurationTime(start_datetime, end_datetime)}
                          selected={selected}
                          onSelect={() => {
                            if (selected) setSelectedPerformance()
                            else setSelectedPerformance(id)
                          }}
                          onPress={() => {
                            if (!token) {
                              toggleLoginModal(true, t('common:make_an_account'))
                            } else if (favorited) {
                              this.setState({ isOpenClosingModal: true, performanceIdToDelete: id })
                            } else {
                              favoriteRoute(id, 'performances', undefined, token, false)
                                .then(() => {
                                  updateProgram(programTypeMap[programType])
                                }).catch(err => console.warn({ err }))
                            }
                          }}
                        />
                      )))}
                    </div>
                  </div>
                </ProgramList>
              ) : (!user.id && <div>{t('common:no_performances_yet')}</div>))}
              {windowWidth && windowWidth <= 730 && programType && programType !== 'partner'
              && (
                <div className="bottomSocials">
                  <button className="navButton navIcon" data-rh>
                    <CopyToClipboard text={href}>
                      <ReactSVG src="/static/images/share.svg" />
                    </CopyToClipboard>
                  </button>
                  <VKShareButton url={href} className="navIcon">
                    {icons.vkontakte({})}
                  </VKShareButton>
                  {/* <FacebookShareButton url={href} className="navIcon">
                    {icons.facebook({})}
                  </FacebookShareButton> */}
                  <TelegramShareButton url={href} className="navIcon">
                    {icons.telegram({})}
                  </TelegramShareButton>
                  {!isServer ? <PDFButton program={program} className="navIcon" />
                    : <Loading className="navIcon" />}
                </div>
              )}
            </div>
          </ProgramSection>
        </Container>
        <Footer />
      </Layout>
    )
  }
}

export default withTranslation(['program', 'about', 'common'])(Program)
